import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ResumePreview from '../components/ResumePreview';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { trackResumePdfDownload } from '../services/analytics';

// ── Constants ─────────────────────────────────────────────────────────────────

const SKILL_CAT_LABELS = {
  legal: 'Legal', transactions: 'Transactions', litigation: 'Litigation',
  compliance: 'Compliance', researchTools: 'Research Tools',
  technology: 'Technology', softSkills: 'Soft Skills', other: 'Other',
};
const SKILL_SUGGESTIONS = {
  legal: [
    'Contract Drafting', 'Due Diligence', 'Legal Research', 'Legal Writing',
    'Case Analysis', 'Document Review', 'Legal Opinions', 'Statutory Interpretation',
    'Corporate Advisory', 'Mergers & Acquisitions', 'Joint Ventures', 'Restructuring',
    'Insolvency & Bankruptcy', 'Dispute Resolution', 'Arbitration', 'Mediation',
    'Intellectual Property', 'Employment Law', 'Real Estate Law', 'Banking & Finance',
    'Competition Law', 'Data Privacy Law', 'Tax Law', 'Securities Law',
  ],
  transactions: [
    'M&A', 'Private Equity', 'Joint Ventures', 'Venture Capital', 'Debt Financing',
    'Structured Finance', 'Project Finance', 'Cross-border Transactions',
    'Share Purchase Agreements', 'Asset Purchase', 'Term Sheets', 'SHA Drafting',
    'Due Diligence Reports', 'Transaction Structuring', 'LBO', 'PIPE Transactions',
  ],
  litigation: [
    'Pleadings', 'Written Submissions', 'Court Appearances', 'Writ Petitions',
    'Civil Litigation', 'Criminal Litigation', 'Appeals', 'Case Strategy',
    'Witness Preparation', 'Evidence Analysis', 'Injunctions', 'Interim Relief',
    'Commercial Disputes', 'Arbitration Proceedings', 'Tribunal Advocacy',
  ],
  compliance: [
    'SEBI Regulations', 'FEMA', 'AML/KYC', 'RBI Guidelines', 'Companies Act 2013',
    'GDPR', 'PDPB Compliance', 'Regulatory Filings', 'Internal Audits',
    'Corporate Governance', 'PMLA', 'Competition Act', 'IRDAI Compliance',
    'Labour Law Compliance', 'Environmental Compliance', 'FCRA',
  ],
  researchTools: [
    'Manupatra', 'SCC Online', 'Westlaw', 'LexisNexis', 'IndiaKanoon',
    'Hein Online', 'JSTOR', 'Kluwer Arbitration', 'Oxford Law', 'OSCOLA',
    'Bluebook Citation', 'AIR Online', 'Bar & Bench', 'Live Law',
  ],
  technology: [
    'MS Word', 'MS Excel', 'MS PowerPoint', 'DocuSign', 'Adobe Acrobat',
    'Contract Lifecycle Management', 'Relativity (eDiscovery)', 'iManage',
    'NetDocuments', 'Litera', 'Zoom', 'Microsoft Teams', 'Google Workspace',
    'Legal Tracker', 'SAP', 'Clio', 'Salesforce',
  ],
  softSkills: [
    'Legal Writing', 'Negotiation', 'Client Communication', 'Research & Analysis',
    'Attention to Detail', 'Time Management', 'Team Collaboration', 'Leadership',
    'Problem Solving', 'Critical Thinking', 'Presentation Skills', 'Drafting',
    'Stakeholder Management', 'Business Development', 'Mentoring',
  ],
  other: [
    'Moot Court', 'Client Counselling', 'Model United Nations', 'Debate',
    'Legal Aid', 'Pro Bono Work', 'Journal Editorial', 'Research Assistant',
    'Event Management', 'Community Outreach', 'Teaching Assistant',
  ],
};
const JP_CATS  = ['legal','transactions','litigation','compliance','researchTools','technology','softSkills'];
const INT_CATS = ['legal','researchTools','technology','other'];

const TEMPLATES = [
  { id: 'classic',   label: 'Classic',   headerBg: '#1a365d', accentColor: '#2b6cb0', headerText: '#fff' },
  { id: 'modern',    label: 'Modern',    headerBg: '#eef2ff', accentColor: '#4f46e5', headerText: '#0f172a', topBar: '#4f46e5' },
  { id: 'minimal',   label: 'Minimal',   headerBg: null,      accentColor: '#111111', headerText: '#111' },
  { id: 'executive', label: 'Executive', headerBg: '#1c1917', accentColor: '#b5853a', headerText: '#d4a843' },
  { id: 'custom',    label: 'Custom',    isCustom: true },
];

const EMPTY_JP = {
  title: 'My Resume', resumeType: 'jobplacement', targetType: 'law-firm', template: 'classic',
  customHeaderBg: '#1a365d', customAccentColor: '#c89b3c',
  personalInfo: { fullName: '', email: '', phone: '', location: '', linkedin: '', barEnrollment: '' },
  summary: '', experience: [], education: [], practiceAreaFocus: '',
  skillCategories: { legal:[], transactions:[], litigation:[], compliance:[], researchTools:[], technology:[], softSkills:[] },
  languages: [], mootCourts: [], publications: [], additionalQualifications: '',
  professionalMemberships: [], commercialExposure: '', awards: [], professionalInterests: '',
};

const EMPTY_INT = {
  title: 'My Resume', resumeType: 'internship', template: 'classic',
  customHeaderBg: '#1a365d', customAccentColor: '#c89b3c',
  personalInfo: { fullName: '', email: '', phone: '', location: '', linkedin: '' },
  aboutMe: '', education: [], experience: [], mootCourts: [], publications: [],
  skillCategories: { legal:[], researchTools:[], technology:[], other:[] },
  languages: [], collegeActivities: [], additionalQualifications: '', areasOfLegalInterest: [],
};

function normalizeForm(data) {
  const base = (data.resumeType === 'internship') ? EMPTY_INT : EMPTY_JP;
  return {
    ...base, ...data,
    personalInfo:     { ...base.personalInfo,     ...(data.personalInfo     || {}) },
    skillCategories:  { ...base.skillCategories,  ...(data.skillCategories  || {}) },
    experience:       data.experience       || [],
    education:        data.education        || [],
    languages:        data.languages        || [],
    mootCourts:       data.mootCourts       || [],
    publications:     data.publications     || [],
    professionalMemberships: data.professionalMemberships || [],
    awards:           data.awards           || [],
    collegeActivities: data.collegeActivities || [],
    areasOfLegalInterest: data.areasOfLegalInterest || [],
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ResumeBuilder() {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const { pathname } = useLocation();
  const { user }   = useAuth();
  const firstName  = user?.name ? user.name.split(' ')[0] : '';
  const isNew      = pathname === '/resume/new';

  const [resumes, setResumes]       = useState([]);
  const [selected, setSelected]     = useState(null);
  const [form, setForm]             = useState(EMPTY_JP);
  const [saveStatus, setSaveStatus] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError]           = useState('');
  const [view, setView]             = useState(id && !isNew ? 'loading' : 'list');

  const [skillInputs, setSkillInputs] = useState({});
  const [skillDropdownOpen, setSkillDropdownOpen] = useState({});
  const [pubInput, setPubInput]       = useState('');
  const [memberInput, setMemberInput] = useState('');
  const [awardInput, setAwardInput]   = useState('');
  const [interestInput, setInterestInput] = useState('');

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError]     = useState('');
  const [showUploadTypeModal, setShowUploadTypeModal] = useState(false);
  const [pendingUploadFile, setPendingUploadFile]     = useState(null);
  const [pendingUploadType, setPendingUploadType]     = useState(null);
  const [uploadMode, setUploadMode]       = useState('improve'); // 'improve' or 'assess'
  const uploadRef = useRef(null);

  // Forwarding email popup state (used on list page)
  const [forwardModal, setForwardModal]   = useState(null); // { resumeId, resumeData | null }
  const [forwardFl, setForwardFl]         = useState({ style: 'formal', recipient: '', orgName: '', practiceArea: '', reason: '', availability: '', duration: 'four to six weeks', yearOfStudy: '' });
  const [forwardFlCopied, setForwardFlCopied] = useState(false);
  const [forwardDownloading, setForwardDownloading] = useState(false);
  const setForwardFlField = (k, v) => setForwardFl(p => ({ ...p, [k]: v }));

  const [aiModal, setAiModal] = useState(null); // { field, value, onDone }

  const formRef     = useRef(form);
  const selectedRef = useRef(selected);
  const saveTimer   = useRef(null);
  const isEditing   = useRef(false);
  const panelRef    = useRef(null);

  useEffect(() => { formRef.current = form; }, [form]);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    if (isNew) {
      isEditing.current = false;
      setSelected(null); setForm(EMPTY_JP);
      setSkillInputs({}); setPubInput(''); setMemberInput(''); setAwardInput(''); setInterestInput('');
      setView('selectType'); setError(''); setSaveStatus('');
    } else if (id) {
      loadResumeById(id);
    } else {
      loadResumes();
    }
  }, [id, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save helpers ───────────────────────────────────────────────────────────
  const saveNow = async () => {
    if (!isEditing.current) return;
    const f   = formRef.current;
    const sel = selectedRef.current;
    setSaveStatus('saving');
    try {
      if (sel) {
        await api.put(`/resume/${sel._id}`, f);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2500);
      } else {
        if (!f.personalInfo?.fullName?.trim()) { setSaveStatus(''); return; }
        const { data } = await api.post('/resume', f);
        isEditing.current = false;
        setSelected(data); setForm(data);
        navigate(`/resume/${data._id}`, { replace: true });
        setTimeout(() => { isEditing.current = true; }, 50);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2500);
      }
    } catch { setSaveStatus('error'); }
  };

  const saveAfterDelay = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(saveNow, 600);
  };

  const handleFormBlur = (e) => {
    if (panelRef.current?.contains(e.relatedTarget)) return;
    saveNow();
  };

  // ── Data loaders / navigation ──────────────────────────────────────────────
  const loadResumes = async () => {
    setView('list'); setSelected(null); setError('');
    try { const { data } = await api.get('/resume'); setResumes(data); }
    catch { setError('Failed to load resumes'); }
  };

  const loadResumeById = async (resumeId) => {
    isEditing.current = false;
    try {
      const { data } = await api.get(`/resume/${resumeId}`);
      setSelected(data); setForm(normalizeForm(data));
      setSkillInputs({}); setPubInput(''); setMemberInput(''); setAwardInput(''); setInterestInput('');
      setView('edit'); setError(''); setSaveStatus('');
    } catch { setError('Failed to load resume'); navigate('/resume'); }
    finally { setTimeout(() => { isEditing.current = true; }, 50); }
  };

  const openNew = () => navigate('/resume/new');

  const chooseType = (type) => {
    isEditing.current = false;
    setSelected(null); setForm(type === 'internship' ? EMPTY_INT : EMPTY_JP);
    setSkillInputs({}); setPubInput(''); setMemberInput(''); setAwardInput(''); setInterestInput('');
    setView('edit'); setError(''); setSaveStatus('');
    setTimeout(() => { isEditing.current = true; }, 50);
  };

  const openEdit = (resumeId) => navigate(`/resume/${resumeId}`);

  const handleBack = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (selectedRef.current) await saveNow();
    navigate('/resume');
  };

  const handleDelete = async (resumeId) => {
    if (!window.confirm('Delete this resume?')) return;
    try {
      await api.delete(`/resume/${resumeId}`);
      await loadResumes();
    } catch { setError('Delete failed'); }
  };

  const handleDownload = async () => {
    if (!selected) return;
    setDownloading(true);
    try {
      const res = await api.get(`/resume/${selected._id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${form.personalInfo?.fullName || 'Resume'}_Resume.pdf`;
      a.click(); URL.revokeObjectURL(url);
      trackResumePdfDownload();
    } catch { setError('PDF download failed'); }
    finally { setDownloading(false); }
  };

  // ── File upload ─────────────────────────────────────────────────────────────
  const openUploadModal = (mode) => {
    setUploadMode(mode);
    setShowUploadTypeModal(true);
  };

  const handleUploadInputChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (pendingUploadType) {
      // Type was already chosen, upload directly
      doUpload(file, pendingUploadType);
      setPendingUploadType(null);
    } else {
      setPendingUploadFile(file);
      setShowUploadTypeModal(true);
    }
  };

  const handleUploadTypeChoice = (resumeType) => {
    if (pendingUploadFile) {
      // File was already selected, upload now
      setShowUploadTypeModal(false);
      doUpload(pendingUploadFile, resumeType);
      setPendingUploadFile(null);
    } else {
      // No file yet - remember the type and open picker
      setPendingUploadType(resumeType);
      setShowUploadTypeModal(false);
      uploadRef.current?.click();
    }
  };

  const doUpload = async (file, resumeType) => {
    setUploadLoading(true); setUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('resumeType', resumeType);
      fd.append('mode', uploadMode);
      const { data } = await api.post('/resume/upload', fd, { timeout: 60000 });
      const resume = data.resume || data;
      const nextStep = data.nextStep;
      if (nextStep === 'assessment') {
        navigate(`/resume/${resume._id}/assess`);
      } else {
        navigate(`/resume/${resume._id}`);
      }
    } catch (e) {
      setUploadError(e.response?.data?.error || 'Upload failed. Please try again.');
    } finally { setUploadLoading(false); }
  };

  // ── Forwarding email popup (list page) ───────────────────────────────────────
  const openForwardModal = async (r) => {
    setForwardModal({ resumeId: r._id, resumeData: null });
    setForwardFl({ style: 'formal', recipient: '', orgName: '', practiceArea: '', reason: '', availability: '', duration: 'four to six weeks', yearOfStudy: '' });
    setForwardFlCopied(false);
    try {
      const { data } = await api.get(`/resume/${r._id}`);
      setForwardModal({ resumeId: r._id, resumeData: data });
    } catch { setForwardModal(null); setError('Could not load resume for forwarding'); }
  };

  const handleForwardDownload = async () => {
    if (!forwardModal?.resumeId) return;
    setForwardDownloading(true);
    try {
      const res = await api.get(`/resume/${forwardModal.resumeId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const name = (forwardModal.resumeData?.personalInfo?.fullName || 'Resume').replace(/\s+/g, '_');
      const a = document.createElement('a'); a.href = url; a.download = `${name}_Resume.pdf`; a.click();
      URL.revokeObjectURL(url); trackResumePdfDownload();
    } catch { setError('PDF download failed'); }
    finally { setForwardDownloading(false); }
  };

  // Download from list card
  const handleCardDownload = async (r) => {
    try {
      const res = await api.get(`/resume/${r._id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const name = (r.personalInfo?.fullName || 'Resume').replace(/\s+/g, '_');
      const a = document.createElement('a'); a.href = url; a.download = `${name}_Resume.pdf`; a.click();
      URL.revokeObjectURL(url); trackResumePdfDownload();
    } catch { setError('PDF download failed'); }
  };

  // ── Form helpers ───────────────────────────────────────────────────────────
  const set = (field, val) => setForm(p => ({ ...p, [field]: val }));
  const updatePI = (f, v) => setForm(p => ({ ...p, personalInfo: { ...p.personalInfo, [f]: v } }));

  const expMax = form.resumeType === 'internship' ? 4 : 6;
  const addExp = () => {
    if ((form.experience?.length || 0) >= expMax) return;
    setForm(p => ({ ...p, experience: [...(p.experience||[]), { company:'', position:'', startDate:'', endDate:'', current:false, description:'' }] }));
  };
  const updateExp = (i,f,v) => setForm(p => { const e=[...p.experience]; e[i]={...e[i],[f]:v}; return {...p,experience:e}; });
  const removeExp = (i) => setForm(p => ({ ...p, experience: p.experience.filter((_,idx)=>idx!==i) }));

  const addEdu = () => setForm(p => ({ ...p, education: [...(p.education||[]), { institution:'', degree:'', field:'', startDate:'', endDate:'', grade:'' }] }));
  const updateEdu = (i,f,v) => setForm(p => { const e=[...p.education]; e[i]={...e[i],[f]:v}; return {...p,education:e}; });
  const removeEdu = (i) => setForm(p => ({ ...p, education: p.education.filter((_,idx)=>idx!==i) }));

  const addSkill = (cat) => {
    const val = (skillInputs[cat]||'').trim(); if (!val) return;
    setForm(p => ({ ...p, skillCategories: { ...(p.skillCategories||{}), [cat]: [...(p.skillCategories?.[cat]||[]), val] } }));
    setSkillInputs(p => ({ ...p, [cat]: '' }));
  };
  const removeSkill = (cat,i) => setForm(p => ({ ...p, skillCategories: { ...(p.skillCategories||{}), [cat]: p.skillCategories[cat].filter((_,idx)=>idx!==i) } }));

  const addLang = () => setForm(p => ({ ...p, languages: [...(p.languages||[]), { language:'', proficiency:'' }] }));
  const updateLang = (i,f,v) => setForm(p => { const l=[...(p.languages||[])]; l[i]={...l[i],[f]:v}; return {...p,languages:l}; });
  const removeLang = (i) => setForm(p => ({ ...p, languages: p.languages.filter((_,idx)=>idx!==i) }));

  const addMoot = () => setForm(p => ({ ...p, mootCourts: [...(p.mootCourts||[]), { competition:'', roundOrRank:'', year:'' }] }));
  const updateMoot = (i,f,v) => setForm(p => { const m=[...(p.mootCourts||[])]; m[i]={...m[i],[f]:v}; return {...p,mootCourts:m}; });
  const removeMoot = (i) => setForm(p => ({ ...p, mootCourts: p.mootCourts.filter((_,idx)=>idx!==i) }));

  const addPub    = () => { if (!pubInput.trim()) return; setForm(p => ({ ...p, publications: [...(p.publications||[]), pubInput.trim()] })); setPubInput(''); };
  const removePub = (i) => setForm(p => ({ ...p, publications: p.publications.filter((_,idx)=>idx!==i) }));

  const addMember    = () => { if (!memberInput.trim()) return; setForm(p => ({ ...p, professionalMemberships: [...(p.professionalMemberships||[]), memberInput.trim()] })); setMemberInput(''); };
  const removeMember = (i) => setForm(p => ({ ...p, professionalMemberships: p.professionalMemberships.filter((_,idx)=>idx!==i) }));

  const addAward    = () => { if (!awardInput.trim()) return; setForm(p => ({ ...p, awards: [...(p.awards||[]), awardInput.trim()] })); setAwardInput(''); };
  const removeAward = (i) => setForm(p => ({ ...p, awards: p.awards.filter((_,idx)=>idx!==i) }));

  const addActivity    = () => setForm(p => ({ ...p, collegeActivities: [...(p.collegeActivities||[]), { activity:'', role:'', year:'' }] }));
  const updateActivity = (i,f,v) => setForm(p => { const c=[...(p.collegeActivities||[])]; c[i]={...c[i],[f]:v}; return {...p,collegeActivities:c}; });
  const removeActivity = (i) => setForm(p => ({ ...p, collegeActivities: p.collegeActivities.filter((_,idx)=>idx!==i) }));

  const addInterest    = () => { if (!interestInput.trim()) return; setForm(p => ({ ...p, areasOfLegalInterest: [...(p.areasOfLegalInterest||[]), interestInput.trim()] })); setInterestInput(''); };
  const removeInterest = (i) => setForm(p => ({ ...p, areasOfLegalInterest: p.areasOfLegalInterest.filter((_,idx)=>idx!==i) }));

  // ── Type selector view ─────────────────────────────────────────────────────
  if (view === 'selectType') {
    return (
      <div style={s.page}>
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>New Resume</h1>
            <p style={s.pageSubtitle}>Start from scratch or upload an existing resume</p>
          </div>
          <button onClick={() => navigate('/resume')} style={s.backLinkBtn}>← Back</button>
        </div>

        <p style={s.orDivider}>Choose your resume type</p>

        <div style={s.typeGrid}>
          {[
            { type: 'jobplacement', label: 'Job Placement', badge: 'For Working Lawyers',
              desc: 'Applying to a law firm or in-house role. Includes Professional Summary, Practice Area Focus, Memberships, and more.',
              icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg> },
            { type: 'internship', label: 'Internship', badge: 'For Law Students',
              desc: 'Applying for an internship or clerkship. Includes About Me, College Activities, and Areas of Legal Interest.',
              icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg> },
          ].map(({ type, label, badge, desc, icon }) => (
            <div key={type} onClick={() => chooseType(type)} style={s.typeCard} className="feature-card">
              <div style={s.typeCardIcon}>{icon}</div>
              <div style={s.typeCardBadge}>{badge}</div>
              <p style={s.typeCardLabel}>{label} Resume</p>
              <p style={s.typeCardDesc}>{desc}</p>
              <div style={s.typeCardCta}>Choose →</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <div style={s.page}>
        <div style={s.pageHeader}>
          <div style={{ flex: '1 1 300px' }}>
            <h1 style={s.pageTitle}>Resume Builder and Assessment</h1>
            <p style={s.pageQuote}>"Your resume is not a document. It is your first conversation with a partner who has never met you."</p>
            <p style={s.pageSubtitle}>Two things you can do here. <strong>Build</strong> a strong legal resume with Nitin's guidance. Or <strong>upload your existing one</strong> and find out honestly where you stand — through a personal, mentor-style assessment you will not get anywhere else.</p>
            <p style={{ fontSize: '13px', color: 'var(--text-3)', fontStyle: 'italic', marginTop: '6px' }}>Three ways to begin — choose what suits you today.</p>
          </div>
          <input ref={uploadRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleUploadInputChange} />
          {resumes.length > 0 && (
            <div style={s.pathButtons}>
              <button onClick={openNew} style={s.pathBtn} className="feature-card" disabled={uploadLoading}>
                <span style={s.pathIcon}>+</span>
                <span style={s.pathLabel}>Build a New Resume</span>
                <span style={s.pathSublabel}>Create from scratch with AskNitin's guidance</span>
              </button>
              <button onClick={() => openUploadModal('improve')} style={s.pathBtn} className="feature-card" disabled={uploadLoading}>
                <span style={s.pathIcon}>↑</span>
                <span style={s.pathLabel}>{uploadLoading && uploadMode === 'improve' ? 'Reading file…' : 'Upload & Improve Mine'}</span>
                <span style={s.pathSublabel}>Upload your existing resume and let AskNitin enhance it</span>
              </button>
              <button onClick={() => openUploadModal('assess')} style={s.pathBtn} className="feature-card" disabled={uploadLoading}>
                <span style={s.pathIcon}>✓</span>
                <span style={s.pathLabel}>{uploadLoading && uploadMode === 'assess' ? 'Reading file…' : 'Assess My Resume'}</span>
                <span style={s.pathSublabel}>Upload your resume as-is and get an honest assessment</span>
              </button>
            </div>
          )}
        </div>

        {uploadError && <Alert type="error" style={{ marginBottom: '16px' }}>{uploadError}</Alert>}
        {error && <Alert type="error">{error}</Alert>}

        {uploadLoading && (
          <div style={s.uploadBanner}>
            <div style={s.uploadSpinner} />
            <div>
              <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>Reading your resume…</p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-3)' }}>We're extracting your details. This takes about 10–20 seconds.</p>
            </div>
          </div>
        )}

        {resumes.length === 0 && !uploadLoading ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--gold)' }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <p style={s.emptyTitle}>{firstName ? `${firstName}, let's build your resume` : 'No resumes yet'}</p>
            <p style={s.emptyDesc}>Build a strong legal resume, improve what you have, or get an honest assessment.</p>
            <div style={s.pathButtons}>
              <button onClick={openNew} style={s.pathBtn} className="feature-card" disabled={uploadLoading}>
                <span style={s.pathIcon}>+</span>
                <span style={s.pathLabel}>Build a New Resume</span>
                <span style={s.pathSublabel}>Create from scratch with AskNitin's guidance</span>
              </button>
              <button onClick={() => openUploadModal('improve')} style={s.pathBtn} className="feature-card" disabled={uploadLoading}>
                <span style={s.pathIcon}>↑</span>
                <span style={s.pathLabel}>{uploadLoading && uploadMode === 'improve' ? 'Reading file…' : 'Upload & Improve Mine'}</span>
                <span style={s.pathSublabel}>Upload your existing resume and let AskNitin enhance it</span>
              </button>
              <button onClick={() => openUploadModal('assess')} style={s.pathBtn} className="feature-card" disabled={uploadLoading}>
                <span style={s.pathIcon}>✓</span>
                <span style={s.pathLabel}>{uploadLoading && uploadMode === 'assess' ? 'Reading file…' : 'Assess My Resume'}</span>
                <span style={s.pathSublabel}>Upload your resume as-is and get an honest assessment</span>
              </button>
            </div>
          </div>
        ) : (
          <div style={s.resumeGrid}>
            {resumes.map((r) => (
              <div key={r._id} style={s.resumeCard} className="feature-card">
                <div style={s.resumeCardTop}>
                  <div style={s.resumeDocIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={s.resumeCardTitle}>{r.title}</p>
                    <p style={s.resumeCardName}>{r.personalInfo?.fullName || 'Untitled'}</p>
                  </div>
                  <span style={{ ...s.typeBadge, ...(r.resumeType === 'internship' ? s.typeBadgeInt : s.typeBadgeJP) }}>
                    {r.resumeType === 'internship' ? 'Internship' : 'Job Placement'}
                  </span>
                </div>
                <p style={s.resumeDate}>Updated {new Date(r.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                <div style={s.resumeCardActions}>
                  <button onClick={() => openEdit(r._id)} style={s.editBtn}>Edit</button>
                  <button onClick={() => handleCardDownload(r)} style={s.downloadCardBtn}>PDF</button>
                  <button onClick={() => openForwardModal(r)} style={s.forwardCardBtn}>Email</button>
                  <button onClick={() => handleDelete(r._id)} style={s.deleteBtn}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  </button>
                </div>
                {r.assessmentStatus === 'completed' && (
                  <button onClick={() => navigate(`/resume/${r._id}/assess`)} style={{ ...s.assessBtn, background: '#d1fae5', color: '#065f46', borderColor: '#bbf7d0' }}>
                    View My Assessment Report
                  </button>
                )}
                {r.assessmentStatus === 'in-progress' && (
                  <button onClick={() => navigate(`/resume/${r._id}/assess`)} style={{ ...s.assessBtn, background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}>
                    Continue Assessment
                  </button>
                )}
                {r.assessmentStatus === 'pending' && r.preserveOriginal && (
                  <button onClick={() => navigate(`/resume/${r._id}/assess`)} style={{ ...s.assessBtn, background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>
                    Take Your Assessment Now
                  </button>
                )}
                {(r.assessmentStatus === 'not-started' || (r.assessmentStatus === 'pending' && !r.preserveOriginal)) && (
                  <button onClick={() => navigate(`/resume/${r._id}/assess`)} style={{ ...s.assessBtn, background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>
                    Start My Assessment
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Forwarding Email Modal */}
        {forwardModal && (
          <ForwardModal
            resumeData={forwardModal.resumeData}
            fl={forwardFl}
            setFlField={setForwardFlField}
            copied={forwardFlCopied}
            setCopied={setForwardFlCopied}
            onDownload={handleForwardDownload}
            downloading={forwardDownloading}
            onClose={() => setForwardModal(null)}
          />
        )}

        {showUploadTypeModal && (
          <div style={s.modalOverlay} onClick={() => { setShowUploadTypeModal(false); setPendingUploadFile(null); }}>
            <div style={s.uploadTypeModal} onClick={e => e.stopPropagation()}>
              <h3 style={s.uploadTypeTitle}>
                {uploadMode === 'assess' ? 'Upload Your Resume for Assessment' : 'Upload & Improve Your Resume'}
              </h3>
              <p style={s.uploadTypeDesc}>
                {uploadMode === 'assess'
                  ? 'Upload your resume as it is — we will not change it. AskNitin will read it and take you straight to a personal, mentor-style assessment of where you stand today.'
                  : 'Upload your existing resume. AskNitin will parse it, let you edit and enhance it in our format, and optionally assess it afterwards.'}
              </p>
              {uploadMode === 'assess' && (
                <p style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '4px', fontStyle: 'italic' }}>Your original resume stays untouched. You can download it back anytime.</p>
              )}
              <p style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '8px' }}>Select your resume type to continue:</p>
              <div style={s.uploadTypeGrid}>
                <button style={s.uploadTypeBtn} onClick={() => handleUploadTypeChoice('jobplacement')}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                  <span style={s.uploadTypeBtnLabel}>Job Placement</span>
                  <span style={s.uploadTypeBtnDesc}>For working lawyers</span>
                </button>
                <button style={s.uploadTypeBtn} onClick={() => handleUploadTypeChoice('internship')}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
                  <span style={s.uploadTypeBtnLabel}>Internship</span>
                  <span style={s.uploadTypeBtnDesc}>For law students</span>
                </button>
              </div>
              <button style={s.uploadTypeCancelBtn} onClick={() => { setShowUploadTypeModal(false); setPendingUploadFile(null); }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Edit view ──────────────────────────────────────────────────────────────
  const isInt = form.resumeType === 'internship';
  const skillCats = isInt ? INT_CATS : JP_CATS;

  return (
    <>
    <div style={s.mobileNotice} className="builder-mobile-notice">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
      </svg>
      Resume Builder and Assessment works best on a desktop or laptop. On mobile, some features may be limited.
    </div>
    <div style={s.builderWrap} className="builder-wrap">
      {/* ── Form panel ─────────────────────────────────────────────── */}
      <div ref={panelRef} style={s.formPanel} className="builder-form-panel" onBlur={handleFormBlur}>
        <div style={s.formHeader} className="builder-form-header">
          <button onClick={handleBack} style={s.backBtn}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back
          </button>
          <h2 style={s.formTitle}>{selected ? form.title || 'Edit Resume' : 'New Resume'}</h2>
          <div style={s.formActions}>
            <span style={s.saveStatus}>
              {saveStatus === 'saving' && <><span style={s.saveDot} />Saving…</>}
              {saveStatus === 'saved'  && <><span style={{ ...s.saveDot, background: 'var(--success)' }} />Saved</>}
              {saveStatus === 'error'  && <span style={{ color: 'var(--danger)', fontSize: '12px' }}>Save failed</span>}
            </span>
            {selected && (
              <button onClick={handleDownload} disabled={downloading} style={s.pdfBtn} className="primary-btn">
                {downloading ? '…' : 'PDF'}
              </button>
            )}
          </div>
        </div>

        {error && <Alert type="error" style={{ margin: '0 16px 12px' }}>{error}</Alert>}

        <div style={s.formBody}>

          {/* General */}
          <Section title="General">
            <Field label="Resume Title">
              <input value={form.title} onChange={e => set('title', e.target.value)} style={s.input} className="nice-input" />
            </Field>
            {!isInt && (
              <Field label="Target Role">
                <select value={form.targetType} onChange={e => { set('targetType', e.target.value); saveAfterDelay(); }} style={s.select} className="nice-input">
                  <option value="law-firm">Law Firm</option>
                  <option value="in-house">In-House / Corporate</option>
                </select>
              </Field>
            )}
            <Field label="PDF Template">
              <div style={s.templateGrid}>
                {TEMPLATES.map(t => {
                  const isActive = form.template === t.id;
                  const thumbBg   = t.isCustom ? (form.customHeaderBg || '#1a365d') : (t.headerBg || '#fff');
                  const thumbAcc  = t.isCustom ? (form.customAccentColor || '#c89b3c') : t.accentColor;
                  const thumbText = t.isCustom ? '#fff' : (t.headerText || '#111');
                  const thumbTopBar = t.isCustom ? null : t.topBar;
                  return (
                    <div
                      key={t.id}
                      onClick={() => { set('template', t.id); saveAfterDelay(); }}
                      style={{ ...s.templateCard, ...(isActive ? s.templateCardActive : { background: 'transparent' }) }}
                    >
                      <div style={{ ...s.templateThumb, background: thumbBg, border: '1px solid #e2e8f0', overflow: 'hidden', position: 'relative' }}>
                        {thumbTopBar && <div style={{ height: '4px', background: thumbTopBar }} />}
                        {!t.headerBg && !t.isCustom ? (
                          <div style={{ height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #ccc' }}>
                            <div style={{ width: '55%', height: '2px', background: '#111', borderRadius: '1px' }} />
                          </div>
                        ) : (
                          <div style={{ height: thumbTopBar ? '14px' : '18px', display: 'flex', alignItems: 'center', paddingLeft: '5px' }}>
                            <div style={{ width: '55%', height: '2px', background: thumbText, borderRadius: '1px', opacity: 0.9 }} />
                          </div>
                        )}
                        <div style={{ padding: '4px 5px', display: 'flex', flexDirection: 'column', gap: '2.5px' }}>
                          <div style={{ height: '1.5px', width: '45%', background: thumbAcc, borderRadius: '1px' }} />
                          <div style={{ height: '1.5px', width: '85%', background: '#ccc', borderRadius: '1px' }} />
                          <div style={{ height: '1.5px', width: '65%', background: '#ccc', borderRadius: '1px' }} />
                          <div style={{ height: '1.5px', width: '45%', background: thumbAcc, borderRadius: '1px', marginTop: '2px' }} />
                          <div style={{ height: '1.5px', width: '75%', background: '#ccc', borderRadius: '1px' }} />
                        </div>
                        {t.isCustom && (
                          <div style={{ position: 'absolute', bottom: '3px', right: '3px', fontSize: '8px', background: 'rgba(0,0,0,0.35)', color: '#fff', borderRadius: '3px', padding: '1px 4px', fontWeight: '600' }}>
                            ✎
                          </div>
                        )}
                      </div>
                      <span style={{ ...s.templateLabel, ...(isActive ? { color: 'var(--navy)', fontWeight: '700' } : {}) }}>{t.label}</span>
                    </div>
                  );
                })}
              </div>
              {form.template === 'custom' && (
                <div style={s.customColorRow}>
                  <div style={s.customColorField}>
                    <label style={s.customColorLabel}>Header Background</label>
                    <div style={s.customColorInputWrap}>
                      <input
                        type="color"
                        value={form.customHeaderBg || '#1a365d'}
                        onChange={e => { set('customHeaderBg', e.target.value); saveAfterDelay(); }}
                        style={s.colorPicker}
                      />
                      <span style={s.colorHex}>{(form.customHeaderBg || '#1a365d').toUpperCase()}</span>
                    </div>
                  </div>
                  <div style={s.customColorField}>
                    <label style={s.customColorLabel}>Accent Color</label>
                    <div style={s.customColorInputWrap}>
                      <input
                        type="color"
                        value={form.customAccentColor || '#c89b3c'}
                        onChange={e => { set('customAccentColor', e.target.value); saveAfterDelay(); }}
                        style={s.colorPicker}
                      />
                      <span style={s.colorHex}>{(form.customAccentColor || '#c89b3c').toUpperCase()}</span>
                    </div>
                  </div>
                </div>
              )}
            </Field>
          </Section>

          {/* Personal Information */}
          <Section title="Personal Information">
            <TwoCol>
              <Field label="Full Name"><input value={form.personalInfo.fullName} onChange={e => updatePI('fullName', e.target.value)} placeholder="e.g. Priya Sharma" style={s.input} className="nice-input" /></Field>
              <Field label="Email"><input value={form.personalInfo.email} onChange={e => updatePI('email', e.target.value)} placeholder="e.g. priya@gmail.com" style={s.input} className="nice-input" /></Field>
            </TwoCol>
            <TwoCol>
              <Field label="Phone"><input value={form.personalInfo.phone} onChange={e => updatePI('phone', e.target.value)} placeholder="e.g. +91 98765 43210" style={s.input} className="nice-input" /></Field>
              <Field label="Location"><input value={form.personalInfo.location} onChange={e => updatePI('location', e.target.value)} placeholder="e.g. Mumbai, Maharashtra" style={s.input} className="nice-input" /></Field>
            </TwoCol>
            <Field label="LinkedIn URL">
              <input value={form.personalInfo.linkedin} onChange={e => updatePI('linkedin', e.target.value)} placeholder="e.g. linkedin.com/in/priya-sharma" style={s.input} className="nice-input" />
            </Field>
            {!isInt && (
              <Field label="Bar Enrollment No.">
                <input value={form.personalInfo.barEnrollment || ''} onChange={e => updatePI('barEnrollment', e.target.value)} placeholder="e.g. MH/1234/2020" style={s.input} className="nice-input" />
              </Field>
            )}
          </Section>

          {/* About Me (internship) */}
          {isInt && (
            <Section title="About Me" hint="2–3 sentences introducing who you are, your year, law school, and what kind of law interests you.">
              <div style={{ position: 'relative' }}>
                <textarea
                  value={form.aboutMe || ''}
                  onChange={e => { if (e.target.value.length <= 350) set('aboutMe', e.target.value); }}
                  placeholder="e.g. I am a 3rd year B.A. LL.B. (Hons) student at NLSIU Bangalore with a keen interest in corporate and commercial law. I am detail-oriented, research-driven, and eager to apply my academic learning in a professional setting."
                  style={{ ...s.input, height: '90px', resize: 'vertical' }}
                  className="nice-input"
                />
                <span style={s.charCount}>{(form.aboutMe||'').length}/350</span>
              </div>
              <button style={s.aiBtn} onClick={() => setAiModal({ field: 'About Me', value: form.aboutMe || '', onDone: v => { if (v.length <= 350) set('aboutMe', v); } })}>✦ AI Assist</button>
            </Section>
          )}

          {/* Professional Summary (job placement) */}
          {!isInt && (
            <Section title="Professional Summary" hint="3–4 sentences summarising your experience, what you specialise in, and what you're looking for.">
              <textarea
                value={form.summary || ''}
                onChange={e => set('summary', e.target.value)}
                placeholder="e.g. Corporate lawyer with 3 years of experience in M&A and private equity transactions at a leading Mumbai law firm. Advised on deals across pharmaceuticals, real estate, and technology sectors. Seeking to deepen expertise in cross-border transactions."
                style={{ ...s.input, height: '90px', resize: 'vertical' }}
                className="nice-input"
              />
              <button style={s.aiBtn} onClick={() => setAiModal({ field: 'Professional Summary', value: form.summary || '', onDone: v => set('summary', v) })}>✦ AI Assist</button>
            </Section>
          )}

          {/* Education */}
          <Section title="Education" action={{ label: '+ Add', fn: addEdu }}>
            {(form.education||[]).map((edu, i) => (
              <EntryCard key={i} label={`Education ${i + 1}`} onRemove={() => removeEdu(i)}>
                <TwoCol>
                  <Field label="Institution"><input value={edu.institution} onChange={e => updateEdu(i,'institution',e.target.value)} placeholder="e.g. NLSIU Bangalore" style={s.input} className="nice-input" /></Field>
                  <Field label="Degree"><input value={edu.degree} onChange={e => updateEdu(i,'degree',e.target.value)} placeholder="e.g. B.A. LL.B. (Hons)" style={s.input} className="nice-input" /></Field>
                </TwoCol>
                <TwoCol>
                  <Field label="Field of Study"><input value={edu.field||''} onChange={e => updateEdu(i,'field',e.target.value)} placeholder="e.g. Corporate Law" style={s.input} className="nice-input" /></Field>
                  <Field label="Grade / GPA"><input value={edu.grade||''} onChange={e => updateEdu(i,'grade',e.target.value)} placeholder="e.g. 7.8 CGPA / 72%" style={s.input} className="nice-input" /></Field>
                </TwoCol>
                <TwoCol>
                  <Field label="Start Year"><input value={edu.startDate||''} onChange={e => updateEdu(i,'startDate',e.target.value)} placeholder="2018" style={s.input} className="nice-input" /></Field>
                  <Field label="End Year"><input value={edu.endDate||''} onChange={e => updateEdu(i,'endDate',e.target.value)} placeholder="2022" style={s.input} className="nice-input" /></Field>
                </TwoCol>
              </EntryCard>
            ))}
          </Section>

          {/* Experience */}
          <Section title={isInt ? `Internship Experience (max ${expMax})` : `Experience (max ${expMax})`} action={{ label: '+ Add', fn: addExp, disabled: (form.experience?.length||0) >= expMax }}>
            {(form.experience||[]).map((exp, i) => (
              <EntryCard key={i} label={isInt ? `Internship ${i + 1}` : `Experience ${i + 1}`} onRemove={() => removeExp(i)}>
                <TwoCol>
                  <Field label={isInt ? 'Role' : 'Position'}><input value={exp.position} onChange={e => updateExp(i,'position',e.target.value)} placeholder={isInt ? 'e.g. Research Intern' : 'e.g. Associate'} style={s.input} className="nice-input" /></Field>
                  <Field label={isInt ? 'Organisation' : 'Company'}><input value={exp.company} onChange={e => updateExp(i,'company',e.target.value)} placeholder={isInt ? 'e.g. AZB & Partners' : 'e.g. Cyril Amarchand Mangaldas'} style={s.input} className="nice-input" /></Field>
                </TwoCol>
                <TwoCol>
                  <Field label="Start Date"><input value={exp.startDate||''} onChange={e => updateExp(i,'startDate',e.target.value)} placeholder="Jan 2022" style={s.input} className="nice-input" /></Field>
                  {!exp.current && <Field label="End Date"><input value={exp.endDate||''} onChange={e => updateExp(i,'endDate',e.target.value)} placeholder="Dec 2023" style={s.input} className="nice-input" /></Field>}
                </TwoCol>
                <label style={s.checkLabel}>
                  <input type="checkbox" checked={exp.current||false} onChange={e => updateExp(i,'current',e.target.checked)} style={{ accentColor: 'var(--navy)' }} />
                  Currently {isInt ? 'interning' : 'working'} here
                </label>
                <Field label="Key Responsibilities / Description">
                  <textarea value={exp.description||''} onChange={e => updateExp(i,'description',e.target.value)} placeholder="e.g. Drafted due diligence reports for M&A transactions; researched regulatory compliance issues under SEBI and Companies Act; assisted in preparing client memos on competition law matters." style={{ ...s.input, height: '72px', resize: 'vertical' }} className="nice-input" />
                  <button style={s.aiBtn} onClick={() => setAiModal({ field: `${isInt ? 'Internship' : 'Work'} Experience: ${exp.position || 'Role'} at ${exp.company || 'Organisation'}`, value: exp.description || '', onDone: v => updateExp(i, 'description', v) })}>✦ AI Assist</button>
                </Field>
              </EntryCard>
            ))}
          </Section>

          {/* Practice Area Focus (job placement only) */}
          {!isInt && (
            <Section title="Practice Area Focus & Career Direction" hint="What type of law do you want to specialise in, and what kind of role are you looking for?">
              <textarea
                value={form.practiceAreaFocus || ''}
                onChange={e => set('practiceAreaFocus', e.target.value)}
                placeholder="e.g. Seeking to build a career in Corporate M&A and Private Equity. Particular interest in cross-border transactions and structured finance. Open to law firm or in-house roles in financial services."
                style={{ ...s.input, height: '72px', resize: 'vertical' }}
                className="nice-input"
              />
              <button style={s.aiBtn} onClick={() => setAiModal({ field: 'Practice Area Focus & Career Direction', value: form.practiceAreaFocus || '', onDone: v => set('practiceAreaFocus', v) })}>✦ AI Assist</button>
            </Section>
          )}

          {/* Skills - categorised */}
          <Section title="Skills">
            {skillCats.map(cat => {
              const query = skillInputs[cat] || '';
              const suggestions = (SKILL_SUGGESTIONS[cat] || []).filter(s =>
                !query || s.toLowerCase().includes(query.toLowerCase())
              );
              const isOpen = !!skillDropdownOpen[cat] && suggestions.length > 0;
              return (
                <div key={cat} style={{ marginBottom: '14px' }}>
                  <p style={s.catLabel}>{SKILL_CAT_LABELS[cat]}</p>
                  <div style={{ position: 'relative' }}>
                    <div style={s.skillRow}>
                      <input
                        value={query}
                        onChange={e => setSkillInputs(p => ({ ...p, [cat]: e.target.value }))}
                        onFocus={() => setSkillDropdownOpen(p => ({ ...p, [cat]: true }))}
                        onBlur={() => setTimeout(() => setSkillDropdownOpen(p => ({ ...p, [cat]: false })), 150)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill(cat))}
                        placeholder={`Type or select a ${SKILL_CAT_LABELS[cat]} skill…`}
                        style={{ ...s.input, flex: 1 }}
                        className="nice-input"
                      />
                      <button onClick={() => addSkill(cat)} style={s.addSkillBtn}>Add</button>
                    </div>
                    {isOpen && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: '60px',
                        background: '#fff', border: '1px solid #dde3ec', borderRadius: '8px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 100,
                        maxHeight: '180px', overflowY: 'auto', marginTop: '2px',
                      }}>
                        {suggestions.map((sug, i) => (
                          <div
                            key={i}
                            onMouseDown={() => {
                              setForm(p => ({ ...p, skillCategories: { ...(p.skillCategories||{}), [cat]: [...(p.skillCategories?.[cat]||[]), sug] } }));
                              setSkillInputs(p => ({ ...p, [cat]: '' }));
                              setSkillDropdownOpen(p => ({ ...p, [cat]: false }));
                            }}
                            style={{
                              padding: '8px 12px', fontSize: '13px', cursor: 'pointer',
                              borderBottom: i < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                              color: '#2d3748',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f7f9fc'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            {sug}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {(form.skillCategories?.[cat]?.length > 0) && (
                    <div style={s.skillsWrap}>
                      {form.skillCategories[cat].map((sk, i) => (
                        <span key={i} style={s.skillTag}>{sk}<button onClick={() => removeSkill(cat, i)} style={s.skillX}>✕</button></span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </Section>

          {/* Languages */}
          <Section title="Languages" action={{ label: '+ Add', fn: addLang }}>
            {(form.languages||[]).map((lang, i) => (
              <div key={i} style={{ ...s.entryCard, marginBottom: '8px', padding: '10px 12px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <Field label="Language"><input value={lang.language} onChange={e => updateLang(i,'language',e.target.value)} style={s.input} className="nice-input" /></Field>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Field label="Proficiency">
                      <select value={lang.proficiency} onChange={e => updateLang(i,'proficiency',e.target.value)} style={s.select} className="nice-input">
                        <option value="">Select…</option>
                        {['Native','Fluent','Conversational','Basic'].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  </div>
                  <button onClick={() => removeLang(i)} style={{ ...s.removeBtn, marginBottom: '10px' }}>Remove</button>
                </div>
              </div>
            ))}
          </Section>

          {/* Moot Courts */}
          <Section title="Moot Court & Academic Competitions" action={{ label: '+ Add', fn: addMoot }}>
            {(form.mootCourts||[]).map((mc, i) => (
              <div key={i} style={{ ...s.entryCard, marginBottom: '8px', padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={s.entryLabel}>Competition {i + 1}</span>
                  <button onClick={() => removeMoot(i)} style={s.removeBtn}>Remove</button>
                </div>
                <Field label="Competition Name"><input value={mc.competition} onChange={e => updateMoot(i,'competition',e.target.value)} placeholder="e.g. Jessup International Moot Court Competition" style={s.input} className="nice-input" /></Field>
                <TwoCol>
                  <Field label="Round / Rank"><input value={mc.roundOrRank} onChange={e => updateMoot(i,'roundOrRank',e.target.value)} placeholder="e.g. Finalist, Winner" style={s.input} className="nice-input" /></Field>
                  <Field label="Year"><input value={mc.year} onChange={e => updateMoot(i,'year',e.target.value)} placeholder="2023" style={s.input} className="nice-input" /></Field>
                </TwoCol>
              </div>
            ))}
          </Section>

          {/* Publications */}
          <Section title="Publications">
            <div style={s.skillRow}>
              <input value={pubInput} onChange={e => setPubInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addPub())} placeholder="Title, Journal, Year…" style={{ ...s.input, flex: 1 }} className="nice-input" />
              <button onClick={addPub} style={s.addSkillBtn}>Add</button>
            </div>
            {(form.publications||[]).map((p, i) => (
              <div key={i} style={s.listItem}>
                <span style={{ flex: 1, fontSize: '12px', color: 'var(--text-2)' }}>{p}</span>
                <button onClick={() => removePub(i)} style={s.skillX}>✕</button>
              </div>
            ))}
          </Section>

          {/* Internship-only: College Activities */}
          {isInt && (
            <Section title="College Activities" action={{ label: '+ Add', fn: addActivity }}>
              {(form.collegeActivities||[]).map((act, i) => (
                <div key={i} style={{ ...s.entryCard, marginBottom: '8px', padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={s.entryLabel}>Activity {i + 1}</span>
                    <button onClick={() => removeActivity(i)} style={s.removeBtn}>Remove</button>
                  </div>
                  <Field label="Activity / Committee"><input value={act.activity} onChange={e => updateActivity(i,'activity',e.target.value)} placeholder="e.g. Student Bar Association" style={s.input} className="nice-input" /></Field>
                  <TwoCol>
                    <Field label="Role / Position"><input value={act.role} onChange={e => updateActivity(i,'role',e.target.value)} placeholder="e.g. Secretary" style={s.input} className="nice-input" /></Field>
                    <Field label="Year"><input value={act.year} onChange={e => updateActivity(i,'year',e.target.value)} placeholder="2023" style={s.input} className="nice-input" /></Field>
                  </TwoCol>
                </div>
              ))}
            </Section>
          )}

          {/* Additional Qualifications */}
          <Section title="Additional Qualifications" hint="Courses, certifications, workshops, or exams that don't fit elsewhere.">
            <textarea
              value={form.additionalQualifications || ''}
              onChange={e => set('additionalQualifications', e.target.value)}
              placeholder="e.g. Certificate in International Arbitration (CIArb, 2023); Completed SEBI Investor Certification Examination; Attended Advanced Contract Drafting Workshop at NLU Delhi."
              style={{ ...s.input, height: '72px', resize: 'vertical' }}
              className="nice-input"
            />
            <button style={s.aiBtn} onClick={() => setAiModal({ field: 'Additional Qualifications', value: form.additionalQualifications || '', onDone: v => set('additionalQualifications', v) })}>✦ AI Assist</button>
          </Section>

          {/* Job Placement-only sections */}
          {!isInt && (<>
            <Section title="Professional Memberships">
              <div style={s.skillRow}>
                <input value={memberInput} onChange={e => setMemberInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addMember())} placeholder="e.g. Bar Council of India, IPBA…" style={{ ...s.input, flex: 1 }} className="nice-input" />
                <button onClick={addMember} style={s.addSkillBtn}>Add</button>
              </div>
              {(form.professionalMemberships||[]).map((m, i) => (
                <div key={i} style={s.listItem}>
                  <span style={{ flex: 1, fontSize: '12px', color: 'var(--text-2)' }}>{m}</span>
                  <button onClick={() => removeMember(i)} style={s.skillX}>✕</button>
                </div>
              ))}
            </Section>

            {form.targetType === 'in-house' && (
              <Section title="Commercial & Business Exposure">
                <textarea
                  value={form.commercialExposure || ''}
                  onChange={e => set('commercialExposure', e.target.value)}
                  placeholder="e.g. Worked on commercial contract negotiations with FMCG clients during internship at XYZ Corp; familiar with procurement processes, vendor agreements, and basic financial statements; exposure to regulatory filings under the Companies Act."
                  style={{ ...s.input, height: '90px', resize: 'vertical' }}
                  className="nice-input"
                />
                <button style={s.aiBtn} onClick={() => setAiModal({ field: 'Commercial & Business Exposure', value: form.commercialExposure || '', onDone: v => set('commercialExposure', v) })}>✦ AI Assist</button>
              </Section>
            )}

            <Section title="Awards & Recognitions">
              <div style={s.skillRow}>
                <input value={awardInput} onChange={e => setAwardInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAward())} placeholder="Award name, organisation, year…" style={{ ...s.input, flex: 1 }} className="nice-input" />
                <button onClick={addAward} style={s.addSkillBtn}>Add</button>
              </div>
              {(form.awards||[]).map((a, i) => (
                <div key={i} style={s.listItem}>
                  <span style={{ flex: 1, fontSize: '12px', color: 'var(--text-2)' }}>{a}</span>
                  <button onClick={() => removeAward(i)} style={s.skillX}>✕</button>
                </div>
              ))}
            </Section>

            <Section title="Professional Interests">
              <textarea
                value={form.professionalInterests || ''}
                onChange={e => set('professionalInterests', e.target.value)}
                placeholder="e.g. Regulatory policy and its intersection with financial markets; ESG compliance frameworks; emerging developments in technology law and data privacy; international commercial arbitration."
                style={{ ...s.input, height: '72px', resize: 'vertical' }}
                className="nice-input"
              />
              <button style={s.aiBtn} onClick={() => setAiModal({ field: 'Professional Interests', value: form.professionalInterests || '', onDone: v => set('professionalInterests', v) })}>✦ AI Assist</button>
            </Section>
          </>)}

          {/* Internship-only: Areas of Legal Interest */}
          {isInt && (
            <Section title="Areas of Legal Interest">
              <div style={s.skillRow}>
                <input value={interestInput} onChange={e => setInterestInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addInterest())} placeholder="e.g. Corporate Law, IP, Criminal…" style={{ ...s.input, flex: 1 }} className="nice-input" />
                <button onClick={addInterest} style={s.addSkillBtn}>Add</button>
              </div>
              {(form.areasOfLegalInterest||[]).length > 0 && (
                <div style={s.skillsWrap}>
                  {form.areasOfLegalInterest.map((a, i) => (
                    <span key={i} style={s.skillTag}>{a}<button onClick={() => removeInterest(i)} style={s.skillX}>✕</button></span>
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* Resume complete banner + PDF download */}
          <div style={s.completeWrap}>
            <div style={s.completeBanner}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>Congratulations! Your resume is now ready.</span>
            </div>
            {selected && (
              <button onClick={handleDownload} disabled={downloading} style={s.completePdfBtn} className="primary-btn">
                {downloading ? 'Generating PDF…' : '⬇ Download PDF'}
              </button>
            )}
          </div>

        </div>
      </div>

      {/* ── Preview panel ───────────────────────────────────────────── */}
      <div style={s.previewPanel} className="builder-preview-panel">
        <div style={s.previewBar}>
          <span className="section-cap">Live Preview</span>
        </div>
        <div style={s.previewScroll}>
          <ResumePreview resume={form} />
        </div>
      </div>
    </div>
    {aiModal && (
      <AIAssistModal
        field={aiModal.field}
        initialValue={aiModal.value}
        resumeType={form.resumeType}
        targetType={form.targetType}
        onDone={(text) => { aiModal.onDone(text); setAiModal(null); }}
        onClose={() => setAiModal(null)}
      />
    )}
    </>
  );
}

// ── Forward Modal (shown on list page) ─────────────────────────────────────────

function ForwardModal({ resumeData, fl, setFlField, copied, setCopied, onDownload, downloading, onClose }) {
  if (!resumeData) {
    return (
      <div style={s.aiOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={{ ...s.aiModal, alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '16px' }}>
          <div style={s.uploadSpinner} />
          <p style={{ fontSize: '14px', color: 'var(--text-2)', margin: 0 }}>Loading resume…</p>
        </div>
      </div>
    );
  }

  const emailText = generateForwardingEmail(resumeData, fl);
  const isInt = resumeData.resumeType === 'internship';
  const isInhouse = !isInt && resumeData.targetType === 'in-house';
  const label = isInt ? 'Forwarding Email - Internship Application'
    : isInhouse ? 'Forwarding Email - In-house Application'
    : 'Forwarding Email - Law Firm Application';

  const lines = emailText.split('\n');
  const subject = lines[0].replace(/^Subject:\s*/i, '');
  const body = lines.slice(2).join('\n');

  const handleSend = () => {
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, '_blank');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(emailText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div style={s.aiOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...s.aiModal, maxWidth: '620px', maxHeight: '90vh' }}>
        <div style={s.aiModalHead}>
          <span style={s.aiModalTitle}>{label}</span>
          <button style={s.aiModalClose} onClick={onClose}>✕</button>
        </div>
        <div style={{ ...s.aiModalBody, gap: '10px' }}>
          {/* Style toggle for internship */}
          {isInt && (
            <div style={s.flStyleRow}>
              {['formal', 'warmer'].map(style => (
                <button key={style} onClick={() => setFlField('style', style)}
                  style={{ ...s.flStyleBtn, ...(fl.style === style ? s.flStyleBtnActive : {}) }}>
                  {style === 'formal' ? 'Formal Tone' : 'Warmer Tone'}
                </button>
              ))}
            </div>
          )}
          <div style={s.twoCol}>
            <Field label="Recipient (e.g. Ms. Sharma)">
              <input value={fl.recipient} onChange={e => setFlField('recipient', e.target.value)} placeholder="Mr. / Ms. Last Name" style={s.input} className="nice-input" />
            </Field>
            <Field label="Organisation / Firm Name">
              <input value={fl.orgName} onChange={e => setFlField('orgName', e.target.value)} placeholder="e.g. AZB & Partners" style={s.input} className="nice-input" />
            </Field>
          </div>
          <div style={s.twoCol}>
            <Field label="Practice Area">
              <input value={fl.practiceArea} onChange={e => setFlField('practiceArea', e.target.value)} placeholder="e.g. Corporate M&A" style={s.input} className="nice-input" />
            </Field>
            {isInt ? (
              <Field label="Year of Study">
                <input value={fl.yearOfStudy} onChange={e => setFlField('yearOfStudy', e.target.value)} placeholder="e.g. 3rd Year" style={s.input} className="nice-input" />
              </Field>
            ) : (
              <Field label="Available From">
                <input value={fl.availability} onChange={e => setFlField('availability', e.target.value)} placeholder="e.g. June 2025" style={s.input} className="nice-input" />
              </Field>
            )}
          </div>
          {isInt && (
            <div style={s.twoCol}>
              <Field label="Availability (start month)">
                <input value={fl.availability} onChange={e => setFlField('availability', e.target.value)} placeholder="e.g. May 2025" style={s.input} className="nice-input" />
              </Field>
              <Field label="Duration">
                <input value={fl.duration} onChange={e => setFlField('duration', e.target.value)} placeholder="e.g. four to six weeks" style={s.input} className="nice-input" />
              </Field>
            </div>
          )}
          <Field label="Why this organisation specifically?">
            <textarea value={fl.reason} onChange={e => setFlField('reason', e.target.value)}
              placeholder={isInhouse ? "e.g. the complexity of your regulatory environment" : "e.g. your work on cross-border transactions"}
              style={{ ...s.input, height: '60px', resize: 'vertical' }} className="nice-input" />
          </Field>
          {/* Generated email preview */}
          <div style={s.flOutputWrap}>
            <div style={s.flOutputHeader}>
              <span style={s.flOutputLabel}>Generated Email</span>
            </div>
            <pre style={s.flOutput}>{emailText}</pre>
          </div>
        </div>
        <div style={{ ...s.aiModalFoot, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleCopy} style={{ ...s.aiCancelBtn, ...(copied ? { color: 'var(--success)', borderColor: 'rgba(26,102,64,0.3)' } : {}) }}>
              {copied ? 'Copied!' : 'Copy Text'}
            </button>
            <button onClick={onDownload} disabled={downloading} style={s.aiCancelBtn}>
              {downloading ? 'Downloading…' : 'Download PDF'}
            </button>
          </div>
          <button onClick={handleSend} style={s.aiDoneBtn}>
            Open in Email App →
          </button>
        </div>
        <p style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', padding: '0 20px 12px', margin: 0 }}>
          Download your PDF first, then attach it manually in your email client.
        </p>
      </div>
    </div>
  );
}

// ── Forwarding Letter (kept for email generation logic) ─────────────────────────

function generateForwardingEmail(form, fl) {
  const pi  = form.personalInfo || {};
  const edu = form.education?.[0] || {};
  const exp = (form.experience || []).filter(e => e.company);

  const fullName    = pi.fullName     || '[Your Full Name]';
  const email       = pi.email        || '[Email]';
  const phone       = pi.phone        || '[Mobile]';
  const linkedin    = pi.linkedin     || '';
  const barEnrol    = pi.barEnrollment || '';
  const school      = edu.institution || '[Law School Name]';
  const degreeStr   = [edu.degree, edu.field ? `in ${edu.field}` : ''].filter(Boolean).join(' ') || '[Degree]';
  const gradYear    = edu.endDate     || '[Year of Graduation]';
  const cgpa        = edu.grade       || '';
  const expNames    = exp.map(e => e.company);

  const recipient   = fl.recipient   || '[Mr. / Ms. Last Name]';
  const orgName     = fl.orgName     || '[Organisation Name]';
  const pa          = fl.practiceArea || '[Practice Area]';
  const reason      = fl.reason      || '[specific reason for choosing this organisation]';
  const avail       = fl.availability || '[proposed month]';
  const duration    = fl.duration    || '[four to six weeks]';
  const yearStudy   = fl.yearOfStudy || '[Year, e.g. 3rd Year]';

  const contactLine = [phone, email].filter(Boolean).join(' · ');

  const sigInt = [fullName, `${yearStudy} · ${degreeStr} · ${school}`, contactLine, linkedin].filter(Boolean).join('\n');
  const sigJP  = [fullName, `${degreeStr} · ${school} · ${gradYear}`, barEnrol ? `Bar Enrolment No. ${barEnrol}` : '', contactLine, linkedin].filter(v => v).join('\n');

  const expLine = expNames.length > 0 ? expNames.join(' and ') : null;

  if (form.resumeType === 'internship') {
    if (fl.style === 'warmer') {
      return [
        `Subject: Internship Application - ${pa} - ${fullName} - ${school}`,
        '',
        `Dear ${recipient},`,
        '',
        `I am ${fullName}, a ${yearStudy} student at ${school} pursuing ${degreeStr}. I am writing to apply for an internship position at ${orgName} in the ${pa} practice.`,
        '',
        `${orgName} stands out to me because ${reason}. My academic and internship experience to date has focused on ${pa}${expLine ? `, and I have previously interned at ${expLine}` : ''}, and I believe an internship with your team would be the right next step in building that foundation.`,
        '',
        `I am available from ${avail} for a period of ${duration}. My resume is attached.`,
        '',
        `I would welcome the opportunity for a brief conversation at a time that suits you.`,
        '',
        `Warm regards,`,
        sigInt,
      ].join('\n');
    }
    // Formal
    const cgpaLine = cgpa ? ` I have a strong academic record (${cgpa}).` : '';
    const expSent  = expLine ? ` I have previously interned at ${expLine}.` : '';
    return [
      `Subject: Application for Internship - ${pa} - ${fullName} - ${school}`,
      '',
      `Dear ${recipient},`,
      '',
      `I am a ${yearStudy} student of ${degreeStr} at ${school} and I write to express my interest in an internship opportunity at ${orgName} in the ${pa} practice.${cgpaLine}${expSent}`,
      '',
      `I am particularly drawn to ${orgName} because ${reason}.`,
      '',
      `I am available for ${duration} beginning ${avail}. I am a quick learner, attentive to detail and will bring genuine commitment to any work assigned to me.`,
      '',
      `I have attached my resume for your kind consideration. I would be grateful for the opportunity to speak with you or a member of your team at your convenience.`,
      '',
      `Thank you for your time.`,
      '',
      `Yours sincerely,`,
      sigInt,
    ].join('\n');
  }

  // Job Placement - In-house
  if (form.targetType === 'in-house') {
    const cgpaNote = cgpa ? ` with a CGPA of ${cgpa}` : '';
    const expSent  = expLine ? expLine : '[Organisation 1] and [Organisation 2]';
    return [
      `Subject: Application for In-house Legal Counsel Position - ${fullName}`,
      '',
      `Dear ${recipient},`,
      '',
      `I am writing to express my interest in joining the legal team at ${orgName} as Legal Counsel / Associate Counsel / Legal Executive.`,
      '',
      `I have recently graduated from ${school} with a focus on ${pa}${cgpaNote}. My internship experience includes ${expSent}, where I was exposed to commercial contract review, regulatory filings and compliance advisory.`,
      '',
      `What draws me to an in-house role at ${orgName} is ${reason}. I am interested in the commercial side of legal work as much as the legal side, and I believe an in-house environment is where I will contribute most effectively.`,
      '',
      `I am available from ${avail} and would welcome the opportunity for a conversation. My resume is attached for your consideration.`,
      '',
      `Thank you.`,
      '',
      `Yours sincerely,`,
      sigJP,
    ].join('\n');
  }

  // Job Placement - Law Firm
  const cgpaNote = cgpa ? ` and a CGPA of ${cgpa}` : '';
  const expSent  = expLine
    ? `During my time at law school I completed internships at ${expLine}, where I worked on matters related to ${pa}.`
    : `During my time at law school I undertook internships where I gained exposure to ${pa} matters.`;
  return [
    `Subject: Application for the Position of Junior Associate / Legal Associate - ${pa} - ${fullName}`,
    '',
    `Dear ${recipient},`,
    '',
    `I write to apply for a position as Junior Associate / Legal Associate in the ${pa} practice at ${orgName}.`,
    '',
    `I have recently graduated from ${school} with a ${degreeStr}${cgpaNote}. ${expSent} These experiences have confirmed my interest in building a career in ${pa} and I am now seeking a full-time role where I can contribute meaningfully from the outset.`,
    '',
    `I am drawn to ${orgName} specifically because ${reason}.`,
    '',
    `I am available to join from ${avail} and am open to discussing the role at your convenience. My resume is attached.`,
    '',
    `Thank you for considering my application.`,
    '',
    `Yours sincerely,`,
    sigJP,
  ].join('\n');
}

function ForwardingLetterSection({ form, fl, setFlField, flCopied, setFlCopied, onDownload, downloading }) {
  const isInt = form.resumeType === 'internship';
  const isInhouse = !isInt && form.targetType === 'in-house';

  const label = isInt ? 'Forwarding Email - Internship Application'
    : isInhouse ? 'Forwarding Email - In-house Application'
    : 'Forwarding Email - Law Firm Application';

  const emailText = generateForwardingEmail(form, fl);

  function handleCopy() {
    navigator.clipboard.writeText(emailText).then(() => {
      setFlCopied(true);
      setTimeout(() => setFlCopied(false), 2000);
    });
  }

  return (
    <div style={s.flWrap}>
      <div style={s.flHeader}>
        <span style={s.flToggleIcon}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
        </span>
        <span style={s.flToggleLabel}>{label}</span>
      </div>

      <div style={s.flBody}>
          {/* Style toggle for internship */}
          {isInt && (
            <div style={s.flStyleRow}>
              {['formal', 'warmer'].map(style => (
                <button
                  key={style}
                  onClick={() => setFlField('style', style)}
                  style={{ ...s.flStyleBtn, ...(fl.style === style ? s.flStyleBtnActive : {}) }}
                >
                  {style === 'formal' ? 'Formal Tone' : 'Warmer Tone'}
                </button>
              ))}
            </div>
          )}

          <div style={s.flFields}>
            <div style={s.twoCol}>
              <Field label="Recipient (Mr./Ms. Last Name)">
                <input value={fl.recipient} onChange={e => setFlField('recipient', e.target.value)} placeholder="e.g. Ms. Sharma" style={s.input} className="nice-input" />
              </Field>
              <Field label="Organisation / Firm Name">
                <input value={fl.orgName} onChange={e => setFlField('orgName', e.target.value)} placeholder="e.g. AZB & Partners" style={s.input} className="nice-input" />
              </Field>
            </div>
            <div style={s.twoCol}>
              <Field label="Practice Area">
                <input value={fl.practiceArea} onChange={e => setFlField('practiceArea', e.target.value)} placeholder="e.g. Corporate M&A" style={s.input} className="nice-input" />
              </Field>
              {isInt ? (
                <Field label="Year of Study">
                  <input value={fl.yearOfStudy} onChange={e => setFlField('yearOfStudy', e.target.value)} placeholder="e.g. 3rd Year" style={s.input} className="nice-input" />
                </Field>
              ) : (
                <Field label="Available From">
                  <input value={fl.availability} onChange={e => setFlField('availability', e.target.value)} placeholder="e.g. June 2025" style={s.input} className="nice-input" />
                </Field>
              )}
            </div>
            {isInt && (
              <div style={s.twoCol}>
                <Field label="Availability (start month)">
                  <input value={fl.availability} onChange={e => setFlField('availability', e.target.value)} placeholder="e.g. May 2025" style={s.input} className="nice-input" />
                </Field>
                <Field label="Duration">
                  <input value={fl.duration} onChange={e => setFlField('duration', e.target.value)} placeholder="e.g. four to six weeks" style={s.input} className="nice-input" />
                </Field>
              </div>
            )}
            <Field label="Specific Reason for Choosing This Organisation">
              <textarea
                value={fl.reason}
                onChange={e => setFlField('reason', e.target.value)}
                placeholder={isInhouse
                  ? "e.g. the complexity of your regulatory environment / the international dimension of your business"
                  : "e.g. your work on cross-border transactions / your reputation in competition law"}
                style={{ ...s.input, height: '72px', resize: 'vertical' }}
                className="nice-input"
              />
            </Field>
          </div>

          {/* Generated email */}
          <div style={s.flOutputWrap}>
            <div style={s.flOutputHeader}>
              <span style={s.flOutputLabel}>Generated Email</span>
              <button onClick={handleCopy} style={{ ...s.flCopyBtn, ...(flCopied ? s.flCopyBtnDone : {}) }}>
                {flCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre style={s.flOutput}>{emailText}</pre>
          </div>

          {onDownload && (
            <button onClick={onDownload} disabled={downloading} style={s.completePdfBtn} className="primary-btn">
              {downloading ? 'Generating PDF…' : '⬇ Download PDF'}
            </button>
          )}
        </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Alert({ type, children, style: extra }) {
  const c = type === 'error'
    ? { bg: 'var(--danger-bg)', border: 'rgba(185,28,28,0.2)', color: 'var(--danger)' }
    : { bg: 'var(--success-bg)', border: 'rgba(26,102,64,0.2)', color: 'var(--success)' };
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.color, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px', ...extra }}>
      {children}
    </div>
  );
}

function Section({ title, children, action, hint }) {
  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <p style={s.sectionTitle}>{title}</p>
        {action && <button onClick={action.fn} disabled={action.disabled} style={{ ...s.addBtn, opacity: action.disabled ? 0.4 : 1 }}>{action.label}</button>}
      </div>
      {hint && <p style={s.sectionHint}>{hint}</p>}
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={s.field}>
      <label style={s.fieldLabel}>{label}</label>
      {children}
    </div>
  );
}

function TwoCol({ children }) {
  return <div style={s.twoCol}>{children}</div>;
}

function EntryCard({ label, children, onRemove }) {
  return (
    <div style={s.entryCard}>
      <div style={s.entryCardHeader}>
        <span style={s.entryLabel}>{label}</span>
        <button onClick={onRemove} style={s.removeBtn}>Remove</button>
      </div>
      {children}
    </div>
  );
}

const AI_FIELD_HINTS = {
  'About Me': {
    what: 'A short personal statement that introduces who you are as a law student.',
    prompts: ['Your year of study and law school', 'Areas of law you are most interested in', 'Key traits, e.g. detail-oriented, research-driven, strong communicator', 'Any academic achievements or standout qualities'],
  },
  'Professional Summary': {
    what: 'A 3–4 sentence overview of your legal career and goals.',
    prompts: ['Your year of study, law school and area of interest', 'One experience that shaped you, internship, moot or activity', 'The role you are applying for, internship or job, firm or in-house'],
  },
  'Practice Area Focus & Career Direction': {
    what: 'A concise statement about the area of law you want to build your career in.',
    prompts: ['Which practice area you are targeting (e.g. Corporate M&A, Disputes)', 'Why you are interested in it', 'Any specific sub-areas or deal types that excite you', 'Law firm vs in-house preference, if relevant'],
  },
  'Additional Qualifications': {
    what: 'Certifications, courses, workshops or exams beyond your degree.',
    prompts: ['Name of each certification or course and the issuing body', 'Year completed', 'Any relevant competitive exams passed (e.g. SEBI, IBBI)'],
  },
  'Commercial & Business Exposure': {
    what: 'Non-legal business experience that makes you valuable as in-house counsel.',
    prompts: ['Any internships or work in a business/corporate setting', 'Familiarity with contracts, procurement, finance, or compliance', 'Industries you have exposure to', 'Any dealings with vendors, clients, or regulators'],
  },
  'Professional Interests': {
    what: 'Legal topics, policy areas or emerging fields you actively follow.',
    prompts: ['Specific areas of law you read or write about outside work', 'Regulatory or policy issues you find interesting', 'Emerging fields like tech law, ESG, arbitration, etc.'],
  },
};

function getExpHint(field) {
  if (field.startsWith('Internship Experience') || field.startsWith('Work Experience')) {
    return {
      what: 'Bullet-style description of what you actually did in this role.',
      prompts: ['Types of tasks, drafting, research, due diligence, client calls, court work', 'Specific laws, regulations or deal types involved', 'Any notable matters or outcomes you contributed to', 'Skills or tools you used'],
    };
  }
  return null;
}

function AIAssistModal({ field, initialValue, resumeType, targetType, onDone, onClose }) {
  const [currentText, setCurrentText] = useState(initialValue || '');
  const [userDetails, setUserDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(false);
  const hasGenerated = useRef(false);

  const hint = AI_FIELD_HINTS[field] || getExpHint(field);

  const handleGenerate = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/resume/ai-assist', { field, currentText, userDetails, resumeType, targetType });
      setCurrentText(data.text);
      hasGenerated.current = true;
      setToast(true);
      setTimeout(() => setToast(false), 2500);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to generate. Please try again.';
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div style={s.aiOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.aiModal}>
        <div style={s.aiModalHead}>
          <span style={s.aiModalTitle}>✦ AI Assist: {field}</span>
          <button style={s.aiModalClose} onClick={onClose}>✕</button>
        </div>
        <div style={s.aiModalBody}>
          {toast && (
            <div style={s.aiToast}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Text generated, review and edit below
            </div>
          )}
          {hint && (
            <div style={s.aiHintBox}>
              <p style={s.aiHintTitle}>{hint.what}</p>
              <p style={{ ...s.aiLabel, marginBottom: '5px', marginTop: '6px' }}>Helpful to mention:</p>
              <ul style={s.aiHintList}>
                {hint.prompts.map((p, i) => <li key={i} style={s.aiHintItem}>{p}</li>)}
              </ul>
            </div>
          )}
          <div>
            <p style={s.aiLabel}>Text</p>
            <textarea
              value={currentText}
              onChange={e => setCurrentText(e.target.value)}
              rows={5}
              style={s.aiTextarea}
              className="nice-input"
              placeholder="Your existing text will appear here. Edit freely or leave blank."
            />
          </div>
          <div>
            <p style={s.aiLabel}>Instructions / additional details</p>
            <textarea
              value={userDetails}
              onChange={e => setUserDetails(e.target.value)}
              rows={2}
              style={s.aiTextarea}
              className="nice-input"
              placeholder={hasGenerated.current ? 'e.g. Make it shorter, add more about M&A, use a confident tone...' : 'Add context, achievements, keywords or anything you would like included...'}
            />
          </div>
          {error && (
            <div style={{ background: 'var(--danger-bg)', border: '1px solid rgba(185,28,28,0.2)', color: 'var(--danger)', padding: '9px 12px', borderRadius: '8px', fontSize: '12px' }}>
              {error}
            </div>
          )}
          <p style={s.aiTokenNote}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Uses 1 token per generation
          </p>
        </div>
        <div style={s.aiModalFoot}>
          <button style={s.aiCancelBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...s.aiGenBtn, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} onClick={handleGenerate} disabled={loading}>
            {loading ? 'Generating…' : hasGenerated.current ? 'Regenerate' : 'Generate'}
          </button>
          {hasGenerated.current && (
            <button style={s.aiDoneBtn} onClick={() => onDone(currentText)}>Use This</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = {
  page:         { maxWidth: '900px', margin: '0 auto', padding: '32px 20px 40px' },
  pageHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '28px', paddingBottom: '20px', borderBottom: '1px solid var(--border-2)' },
  pageTitle:    { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '30px', fontWeight: '700', color: 'var(--navy)', marginBottom: '4px' },
  pageQuote:    { fontSize: '14px', color: 'var(--gold)', fontStyle: 'italic', margin: '4px 0 6px', lineHeight: '1.5', fontFamily: "'Playfair Display', Georgia, serif" },
  pageSubtitle: { fontSize: '14px', color: 'var(--text-2)', lineHeight: '1.6' },
  primaryBtn:   { background: 'var(--navy)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  backLinkBtn:  { background: 'none', border: '1px solid var(--border)', color: 'var(--text-2)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },

  typeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', maxWidth: '560px' },
  typeCard: { background: '#fff', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: 'var(--shadow-sm)', transition: 'transform 0.15s, box-shadow 0.15s' },
  typeCardIcon:  { width: '48px', height: '48px', borderRadius: '12px', background: 'var(--gold-light)', border: '1px solid var(--gold-mid)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--navy)', marginBottom: '4px' },
  typeCardBadge: { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gold)', background: 'var(--gold-light)', border: '1px solid var(--gold-mid)', padding: '2px 8px', borderRadius: '99px', alignSelf: 'flex-start' },
  typeCardLabel: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17px', fontWeight: '700', color: 'var(--navy)', margin: 0 },
  typeCardDesc:  { fontSize: '12px', color: 'var(--text-3)', lineHeight: '1.55', margin: 0 },
  typeCardCta:   { fontSize: '12px', fontWeight: '700', color: 'var(--navy-2)', marginTop: '4px' },

  empty:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '60px 20px', textAlign: 'center' },
  emptyIcon:  { width: '56px', height: '56px', borderRadius: '14px', background: 'var(--gold-light)', border: '1px solid var(--gold-mid)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px' },
  emptyTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '18px', fontWeight: '700', color: 'var(--navy)' },
  emptyDesc:  { fontSize: '13px', color: 'var(--text-3)' },

  resumeGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' },
  resumeCard:       { background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px', boxShadow: 'var(--shadow-sm)' },
  resumeCardTop:    { display: 'flex', alignItems: 'flex-start', gap: '12px' },
  resumeDocIcon:    { width: '40px', height: '40px', borderRadius: '10px', background: 'var(--gold-light)', color: 'var(--navy-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  resumeCardTitle:  { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '15px', fontWeight: '700', color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  resumeCardName:   { fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' },
  resumeDate:       { fontSize: '11px', color: 'var(--text-3)' },
  resumeCardActions: { display: 'flex', gap: '6px', marginTop: '4px' },
  editBtn:          { flex: 1, background: 'var(--navy)', color: '#fff', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  deleteBtn:        { width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: 'var(--danger)', border: '1px solid rgba(185,28,28,0.25)', borderRadius: '8px', cursor: 'pointer', flexShrink: 0 },
  assessBtn:        { width: '100%', border: '1px solid', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', marginTop: '6px', fontFamily: "'DM Sans',sans-serif" },
  downloadCardBtn:  { display: 'flex', alignItems: 'center', gap: '4px', background: '#fff', color: 'var(--navy-2)', border: '1px solid var(--border)', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  forwardCardBtn:   { display: 'flex', alignItems: 'center', gap: '4px', background: '#fff', color: 'var(--navy-2)', border: '1px solid var(--border)', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  uploadBtn:        { background: '#fff', border: '1px solid var(--border)', color: 'var(--navy-2)', padding: '9px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  pathButtons:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', width: '100%', marginTop: '8px' },
  pathBtn:          { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 16px', cursor: 'pointer', textAlign: 'center', boxShadow: 'var(--shadow-sm)', transition: 'transform 0.15s, box-shadow 0.15s', fontFamily: "'DM Sans', sans-serif" },
  pathIcon:         { width: '40px', height: '40px', borderRadius: '10px', background: 'var(--gold-light)', border: '1px solid var(--gold-mid)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: 'var(--navy)', fontWeight: '700' },
  pathLabel:        { fontSize: '14px', fontWeight: '700', color: 'var(--navy)' },
  pathSublabel:     { fontSize: '11px', color: 'var(--text-3)', lineHeight: '1.4' },
  uploadBanner:     { display: 'flex', alignItems: 'center', gap: '14px', background: 'var(--gold-light)', border: '1px solid var(--gold-mid)', borderRadius: '12px', padding: '14px 18px', marginBottom: '24px' },
  uploadSpinner:    { width: '20px', height: '20px', borderRadius: '50%', border: '2.5px solid var(--gold-mid)', borderTopColor: 'var(--gold)', flexShrink: 0, animation: 'spin 0.8s linear infinite' },
  uploadCard:       { display: 'flex', alignItems: 'center', gap: '16px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px 20px', marginBottom: '12px', flexWrap: 'wrap' },
  uploadCardIcon:   { width: '44px', height: '44px', borderRadius: '10px', background: 'var(--gold-light)', border: '1px solid var(--gold-mid)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--navy)', flexShrink: 0 },
  uploadCardTitle:  { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '15px', fontWeight: '700', color: 'var(--navy)', margin: '0 0 4px' },
  uploadCardDesc:   { fontSize: '12px', color: 'var(--text-3)', lineHeight: '1.55', margin: 0 },
  uploadCardBtn:    { background: 'var(--navy)', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 },
  orDivider:        { textAlign: 'center', fontSize: '12px', color: 'var(--text-3)', margin: '20px 0 16px', letterSpacing: '0.5px' },
  typeBadge:    { fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px', whiteSpace: 'nowrap', flexShrink: 0, marginTop: '2px' },
  typeBadgeJP:  { background: 'var(--navy)', color: '#fff' },
  typeBadgeInt: { background: 'var(--gold-light)', color: 'var(--gold)', border: '1px solid var(--gold-mid)' },

  builderWrap: { display: 'flex', height: 'calc(100dvh - 60px)', overflow: 'hidden' },
  formPanel:   { width: '420px', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border)', background: '#fff', overflowY: 'auto' },
  formHeader:  { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: '1px solid var(--border-2)', background: 'var(--bg)', position: 'sticky', top: 0, zIndex: 10 },
  backBtn:     { display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: '13px', padding: '4px', fontWeight: '500' },
  formTitle:   { fontFamily: "'Playfair Display', Georgia, serif", flex: 1, fontSize: '15px', fontWeight: '700', color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  formActions: { display: 'flex', alignItems: 'center', gap: '8px' },
  saveStatus:  { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--text-3)', fontWeight: '500' },
  saveDot:     { display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--navy)', opacity: 0.7 },
  pdfBtn:      { background: 'var(--gold)', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  formBody:    { flex: 1, padding: '16px', display: 'flex', flexDirection: 'column' },

  section:       { marginBottom: '20px' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid var(--border-2)' },
  sectionTitle:  { fontSize: '11px', fontWeight: '700', color: 'var(--navy-2)', textTransform: 'uppercase', letterSpacing: '1px' },
  sectionHint:   { fontSize: '11.5px', color: 'var(--text-3)', marginBottom: '8px', marginTop: '-2px', lineHeight: '1.5' },
  addBtn:        { background: 'none', border: '1px solid var(--border)', color: 'var(--text-2)', padding: '3px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' },

  field:      { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' },
  fieldLabel: { fontSize: '11px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:      { width: '100%', padding: '8px 11px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', color: 'var(--text)', transition: 'border-color 0.15s, box-shadow 0.15s', boxSizing: 'border-box' },
  select:     { width: '100%', padding: '8px 11px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', color: 'var(--text)', boxSizing: 'border-box', cursor: 'pointer' },
  twoCol:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },

  entryCard:       { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px', marginBottom: '10px' },
  entryCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  entryLabel:      { fontSize: '11px', fontWeight: '700', color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  removeBtn:       { background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '11px', fontWeight: '600' },
  checkLabel:      { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-2)', marginBottom: '10px', cursor: 'pointer' },

  catLabel:    { fontSize: '11px', fontWeight: '700', color: 'var(--navy-2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  skillRow:    { display: 'flex', gap: '8px', marginBottom: '6px' },
  addSkillBtn: { background: 'var(--navy)', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' },
  skillsWrap:  { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' },
  skillTag:    { background: 'var(--gold-light)', border: '1px solid var(--gold-mid)', color: 'var(--navy-2)', padding: '4px 10px', borderRadius: '99px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500' },
  skillX:      { background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontSize: '11px', padding: '0', lineHeight: 1, fontWeight: '700' },

  listItem: { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '6px' },

  charCount: { position: 'absolute', bottom: '8px', right: '12px', fontSize: '10px', color: 'var(--text-3)' },

  templateGrid:       { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginTop: '4px' },
  templateCard:       { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', cursor: 'pointer', borderRadius: '8px', padding: '6px', border: '2px solid transparent', transition: 'border-color 0.15s, background 0.15s' },
  templateCardActive: { border: '2px solid var(--navy)', background: 'var(--gold-light)' },
  templateThumb:      { width: '100%', height: '68px', borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' },
  templateLabel:      { fontSize: '10px', color: 'var(--text-3)', fontWeight: '500', textAlign: 'center' },
  customColorRow:       { display: 'flex', gap: '12px', marginTop: '10px', padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '10px' },
  customColorField:     { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 },
  customColorLabel:     { fontSize: '11px', fontWeight: '600', color: 'var(--text-2)' },
  customColorInputWrap: { display: 'flex', alignItems: 'center', gap: '8px' },
  colorPicker:          { width: '32px', height: '32px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', padding: '2px', background: 'none' },
  colorHex:             { fontSize: '12px', fontWeight: '500', color: 'var(--text-2)', fontFamily: 'monospace' },

  previewPanel:  { flex: 1, background: 'var(--bg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  previewBar:    { padding: '10px 16px', background: '#fff', borderBottom: '1px solid var(--border-2)' },
  previewScroll: { flex: 1, overflowY: 'auto', padding: '24px' },

  flWrap:          { margin: '0 0 12px', borderTop: '1px solid var(--border-2)' },
  flHeader:        { display: 'flex', alignItems: 'center', gap: '8px', padding: '14px 0 10px' },
  flToggleIcon:    { color: 'var(--navy)', flexShrink: 0 },
  flToggleLabel:   { flex: 1, fontSize: '13px', fontWeight: '700', color: 'var(--navy)', fontFamily: "'Playfair Display', Georgia, serif" },
  completeWrap:    { margin: '8px 0 4px', display: 'flex', flexDirection: 'column', gap: '10px' },
  completeBanner:  { display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--success-bg)', border: '1px solid rgba(26,102,64,0.2)', color: 'var(--success)', padding: '12px 14px', borderRadius: '10px', fontSize: '13px', fontWeight: '600' },
  completePdfBtn:  { background: 'var(--gold)', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', alignSelf: 'flex-start' },
  flBody:          { paddingBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  flStyleRow:      { display: 'flex', gap: '8px' },
  flStyleBtn:      { flex: 1, padding: '7px', border: '1.5px solid var(--border)', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: 'var(--text-2)', transition: 'all 0.15s' },
  flStyleBtnActive: { border: '1.5px solid var(--navy)', background: 'var(--gold-light)', color: 'var(--navy)' },
  flFields:         { display: 'flex', flexDirection: 'column', gap: '8px' },
  flOutputWrap:     { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' },
  flOutputHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border-2)', background: '#fff' },
  flOutputLabel:    { fontSize: '12px', fontWeight: '600', color: 'var(--text-2)' },
  flCopyBtn:        { background: 'var(--navy)', color: '#fff', border: 'none', padding: '5px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', transition: 'background 0.15s' },
  flCopyBtnDone:    { background: 'var(--success, #166534)' },
  flOutput:         { padding: '12px', fontSize: '11.5px', lineHeight: '1.65', color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'inherit', overflowX: 'auto' },

  mobileNotice: { display: 'none', alignItems: 'center', gap: '8px', background: 'var(--warning-bg)', border: '1px solid rgba(146,64,14,0.2)', color: 'var(--warning)', padding: '10px 14px', fontSize: '12px', fontWeight: '500' },
  aiBtn: { display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '6px', background: 'linear-gradient(135deg, var(--navy-2), var(--navy))', color: '#fff', border: 'none', padding: '6px 13px', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', letterSpacing: '0.2px' },

  // AI Assist Modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(12,35,64,0.55)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  uploadTypeModal: { background: '#fff', borderRadius: '18px', padding: '28px 24px', maxWidth: '420px', width: '100%', boxShadow: '0 8px 40px rgba(12,35,64,0.18)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', textAlign: 'center' },
  uploadTypeTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '18px', fontWeight: '700', color: 'var(--navy)', margin: 0 },
  uploadTypeDesc:  { fontSize: '13px', color: 'var(--text-2)', margin: 0 },
  uploadTypeGrid:  { display: 'flex', gap: '14px', width: '100%' },
  uploadTypeBtn:   { flex: 1, background: '#fff', border: '1.5px solid var(--border)', borderRadius: '14px', padding: '20px 14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', transition: 'border-color 0.15s, box-shadow 0.15s', color: 'var(--navy)' },
  uploadTypeBtnLabel: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '15px', fontWeight: '700' },
  uploadTypeBtnDesc:  { fontSize: '11px', color: 'var(--text-3)' },
  uploadTypeCancelBtn: { background: 'none', border: 'none', color: 'var(--text-3)', fontSize: '13px', cursor: 'pointer', padding: '4px 12px' },
  aiOverlay:    { position: 'fixed', inset: 0, background: 'rgba(12,35,64,0.55)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  aiModal:      { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(12,35,64,0.22)', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' },
  aiModalHead:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-2)' },
  aiModalTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '16px', fontWeight: '700', color: 'var(--navy)' },
  aiModalClose: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: '18px', lineHeight: 1, padding: '2px 4px' },
  aiModalBody:  { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto' },
  aiLabel:      { fontSize: '11px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
  aiTextarea:   { width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: '9px', fontSize: '13px', lineHeight: '1.6', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', color: 'var(--text)' },
  aiResultBox:  { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '9px', padding: '10px 12px', fontSize: '13px', lineHeight: '1.65', color: 'var(--text)', whiteSpace: 'pre-wrap', minHeight: '60px' },
  aiModalFoot:  { display: 'flex', gap: '10px', padding: '12px 20px', borderTop: '1px solid var(--border-2)', justifyContent: 'flex-end' },
  aiCancelBtn:  { background: 'none', border: '1px solid var(--border)', color: 'var(--text-2)', padding: '8px 18px', borderRadius: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  aiGenBtn:     { background: 'var(--navy)', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  aiDoneBtn:    { background: 'var(--gold)', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
  aiTokenNote:  { fontSize: '11px', color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '4px' },
  aiToast:      { display: 'flex', alignItems: 'center', gap: '7px', background: 'var(--success-bg)', border: '1px solid rgba(26,102,64,0.2)', color: 'var(--success)', padding: '9px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', animation: 'fadeUp 0.2s ease forwards' },
  aiHintBox:    { background: 'var(--gold-light)', border: '1px solid var(--gold-mid)', borderRadius: '9px', padding: '10px 13px' },
  aiHintTitle:  { fontSize: '12px', color: 'var(--navy-2)', fontWeight: '500', lineHeight: '1.5' },
  aiHintList:   { paddingLeft: '16px', margin: 0 },
  aiHintItem:   { fontSize: '12px', color: 'var(--text-2)', lineHeight: '1.8' },
};
